import React, { useCallback, useState } from 'react';
import { useWeb3React } from '@web3-react/core';
import Button from 'react-bootstrap/Button';
import axios from 'axios';
import { toast } from 'react-hot-toast';

import { toEther, formatEther } from '../util';
import useRainbowBalance from '../hooks/useRainbowBalance';
import useTokenVault from '../hooks/useTokenVault';
import useVaultBalance from '../hooks/useVaultBalance';

const SYNC_BLOCK_BUFFER =
  parseInt(process.env.NEXT_PUBLIC_SYNC_BLOCK_BUFFER) || 1;

const RainbowBalancePanel = ({ enableWithdrawals }) => {
  const { account } = useWeb3React();
  const rainbowBalance = useRainbowBalance();
  const tokenVault = useTokenVault();
  const vaultBalance = useVaultBalance();

  const [withdrawing, setWithdrawing] = useState(false);

  const withdraw = useCallback(async () => {
    if (tokenVault && account) {
      const req = await axios.post('/api/withdraw', null, {
        params: { account },
      });

      const { amount, tax, nonce, timestamp, signature } = req.data.withdrawal;

      tokenVault
        .withdraw(amount, tax, nonce, timestamp, signature)
        .then((tx) => {
          toast.loading(`Withdrawing ${formatEther(amount)} rainbows...`, {
            id: 'withdraw',
          });
          setWithdrawing(true);
          return tx.wait(SYNC_BLOCK_BUFFER + 1);
        })
        .then(() => {
          toast.success(`Withdrawal successful!`, { id: 'withdraw' });
        })
        .catch((e) => toast.error(`Error: ${e.message}`, { id: 'withdraw' }))
        .finally(() => setWithdrawing(false));
    }
  }, [tokenVault, account]);

  const insufficientBalance = vaultBalance.lt(
    rainbowBalance.pending.available.total
  );

  const disableWithdraw =
    insufficientBalance ||
    toEther(rainbowBalance.pending.available.total) < 1 ||
    withdrawing;

  const dailyRainbows = rainbowBalance.daily.genesis.add(
    rainbowBalance.daily.companions
  );

  return (
    <>
      <h2 className="display-3 title mb-3">Your Rainbows</h2>
      <div>
        <h3>Wallet balance</h3>
        <p className="fw-bold">
          <span className="fs-2">{formatEther(rainbowBalance.current)}</span>{' '}
          $RAINBOWS
        </p>
        <hr />
        {enableWithdrawals ? (
          ''
        ) : (
          <div className="alert alert-warning" role="alert">
            Rainbow withdrawals are temporarily disabled while we transition to
            active staking. Follow the announcements in our{' '}
            <a
              href="https://discord.gg/noundles"
              target="_blank"
              rel="noreferrer"
            >
              Discord
            </a>{' '}
            for more details.
          </div>
        )}

        <h4>Bank balance</h4>
        <div className="d-flex flex-column flex-md-row">
          <p>
            <span className="fw-bold">
              <span className="fs-2">
                {formatEther(rainbowBalance.pending.available.total)}
              </span>{' '}
              $RAINBOWS
            </span>
            {enableWithdrawals ? (
              <>
                <br />
                <small className="text-muted">
                  Withdrawal tax:{' '}
                  {formatEther(rainbowBalance.pending.available.tax, 3)}{' '}
                  $RAINBOWS
                </small>
              </>
            ) : (
              ''
            )}
          </p>
          <div className="ms-md-auto">
            {enableWithdrawals ? (
              <>
                <Button
                  onClick={withdraw}
                  disabled={disableWithdraw}
                  size="lg"
                  className="btn-dark rounded-pill px-4"
                >
                  {withdrawing ? (
                    <>
                      <span
                        className="spinner-border me-2 align-bottom"
                        role="status"
                        aria-hidden="true"
                      ></span>{' '}
                      Withdrawing...
                    </>
                  ) : (
                    <>Withdraw $Rainbows</>
                  )}
                </Button>
                {insufficientBalance ? (
                  <div className="text-end my-1 text-danger fw-bold">
                    <small>Rainbow Vault is empty!</small>
                  </div>
                ) : (
                  ''
                )}
              </>
            ) : (
              ''
            )}
          </div>
        </div>
        <div
          className="mt-3 alert alert-info text-center text-dark fw-bold"
          role="alert"
        >
          You are earning {formatEther(dailyRainbows)} $RAINBOWS a day!
        </div>
        <div className="alert alert-info" role="alert">
          Bank balance calculations are under active development. Let us know
          if you notice any issues.
        </div>
        <hr />
        <div className="row my-2">
          <h5>Lifetime earnings</h5>
          <div className="col-12 col-md-6">
            <p>
              Earned by Noundles <br />
              <span className="fw-bold">
                {formatEther(rainbowBalance.pending.genesis)}
              </span>
            </p>
          </div>
          <div className="col-12 col-md-6">
            <p>
              Earned by Frendles <br />
              <span className="fw-bold">
                {formatEther(rainbowBalance.pending.companions)}
              </span>
            </p>
          </div>
          <div className="col-12 col-md-6">
            <p>
              Earned by Ruggles <br />
              <span className="fw-bold">
                {formatEther(rainbowBalance.pending.evil)}
              </span>
            </p>
          </div>
        </div>
        <div className="row my-2">
          <h5>Withdrawals</h5>
          <div className="col-12 col-md-6">
            <p>
              Total amount <br />
              <span className="fw-bold">
                {formatEther(rainbowBalance.withdrawals)}
              </span>
            </p>
          </div>
        </div>

        <div className="my-2 text-muted">
          Need more $RAINBOWS?{' '}
          <a
            href="https://app.uniswap.org/#/swap?chain=mainnet&inputCurrency=ETH&outputCurrency=0x54a7cee7b02976ace1bdd4afad87273251ed34cf"
            className="link-secondary"
            target="_blank"
            rel="noreferrer"
          >
            Click here
          </a>
          .
        </div>
      </div>
    </>
  );
};

export default RainbowBalancePanel;
